import React, { useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';

export const CHAT_Z_INDEX = 2147483647;

export const ChatLauncher = ({ onOpen }) => (
  <button
    type="button"
    data-testid="chat-widget-toggle"
    onClick={onOpen}
    aria-label="Open chat with Jess's assistant"
    className="fixed bottom-48 right-4 sm:right-6 w-16 h-16 rounded-full bg-gradient-to-br from-[#143160] to-[#1E4A86] text-gold-400 shadow-2xl border-2 border-gold-400/50 flex items-center justify-center hover:scale-110 transition-transform"
    style={{ boxShadow: '0 8px 32px rgba(20,49,96,0.5)', zIndex: CHAT_Z_INDEX }}
  >
    <MessageCircle className="w-8 h-8" />
    <span className="absolute -top-1 -right-1 w-4 h-4 bg-gold-400 rounded-full animate-ping" />
  </button>
);

export const ChatHeader = ({ onClose }) => (
  <div className="bg-gradient-to-r from-[#143160] to-[#1E4A86] text-white px-4 py-3 flex items-center justify-between">
    <div className="flex items-center space-x-2">
      <span className="w-9 h-9 rounded-full bg-gold-400 text-[#143160] font-bold flex items-center justify-center">J</span>
      <div>
        <p className="font-bold text-sm leading-tight">Chat with Jess's Assistant</p>
        <p className="text-[11px] text-gold-300">Coastal VA &amp; NC real estate</p>
      </div>
    </div>
    <button type="button" data-testid="chat-widget-close" onClick={onClose} aria-label="Close chat" className="p-1 hover:bg-white/10 rounded-lg">
      <X className="w-5 h-5" />
    </button>
  </div>
);

export const ChatMessage = ({ message, pending }) => {
  const isUser = message.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
          isUser ? 'bg-[#143160] text-white rounded-br-sm' : 'bg-white text-gray-800 border border-gray-100 rounded-bl-sm shadow-sm'
        }`}
      >
        {message.content || (pending ? '…' : '')}
      </div>
    </div>
  );
};

export const ChatComposer = ({ onSend, disabled }) => {
  const [input, setInput] = useState('');

  const submit = () => {
    const text = input.trim();
    if (!text || disabled) return;
    setInput('');
    onSend(text);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="p-3 border-t border-gray-100 bg-white">
      <div className="flex items-end space-x-2">
        <textarea
          data-testid="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder="Ask about the coast, buying, selling…"
          className="flex-1 resize-none max-h-24 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gold-400"
        />
        <button
          type="button"
          data-testid="chat-send"
          onClick={submit}
          disabled={disabled || !input.trim()}
          aria-label="Send message"
          className="w-10 h-10 flex-shrink-0 rounded-xl bg-gradient-to-r from-gold-400 to-gold-500 text-[#143160] flex items-center justify-center disabled:opacity-50 hover:scale-105 transition-transform"
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
      <p className="text-[10px] text-gray-400 text-center mt-2">AI assistant · For specifics, call Jess at 252-564-5612</p>
    </div>
  );
};
